import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme, useMediaQuery } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import PrintIcon from '@mui/icons-material/Print';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import AuthContext from '../../authAndConnections/auth';
import AxiosGlobal from '../../authAndConnections/axiosGlobalUrl';
import { printHtmlDocument } from '../../../tools/printDocument';
import { uiLang } from '../docFormat';

// The collective list: one row per packing list, the register that used to be kept by hand in a spreadsheet, for the SAME search,
// time frame and filters as the list behind it (they are shared, so a filter set here is set there). What the columns and rows are
// lives on the server (api/utils/packingListCollective.js), which also prints it, so the table and the paper cannot differ.
// A person's choice of columns is a convenience, kept in this browser. The print language is not kept: every opening starts on the app's.
const COLUMNS_KEY = 'xms_pl_collective_columns';
const PRINT_LANGS = [['en', 'English'], ['ar', 'العربية'], ['fa', 'فارسی']];
const readChosen = () => {
  try {
    const list = JSON.parse(localStorage.getItem(COLUMNS_KEY));
    return Array.isArray(list) && list.length ? list : null;
  } catch (_) { return null; }
};
const writeChosen = (list) => {
  try {
    if (list) localStorage.setItem(COLUMNS_KEY, JSON.stringify(list)); else localStorage.removeItem(COLUMNS_KEY);
  } catch (_) { /* a private window: the choice lasts as long as the screen */ }
};

export default function PackingListCollective({
  open, onClose, T, branchId, search, onSearch, debouncedSearch, type, filters, picksJson, calendar, filterCount,
  activeFilters, captionFor, refreshKey, onOpenFilters, onOpenList,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMob = useMediaQuery(theme.breakpoints.down('md'));
  const authCtx = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);
  const api = `${axiosGlobal.defaultTargetApi}/mis/packing-lists/collective`;
  const lang = uiLang(i18n);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sort, setSort] = useState({ key: 'date', dir: 'asc' });
  const [chosen, setChosenState] = useState(readChosen);   // null = the columns the server says are on by default
  const [columnsAnchor, setColumnsAnchor] = useState(null);
  const [printChoice, setPrintChoice] = useState(null);   // null = the app's language
  const [printing, setPrinting] = useState(false);
  const requestRef = useRef(0);

  const setChosen = (list) => { setChosenState(list); writeChosen(list); };
  const printLang = printChoice || lang;
  useEffect(() => { if (open) setPrintChoice(null); }, [open]);

  const params = useMemo(() => ({
    branchId, lang, dateSystem: calendar, sort: sort.key, dir: sort.dir,
    ...(type && type !== 'all' ? { type } : {}),
    ...(debouncedSearch && debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
    ...(picksJson !== '{}' ? { filters: picksJson } : {}),
  }), [branchId, lang, calendar, sort, type, debouncedSearch, filters.status, filters.dateFrom, filters.dateTo, picksJson]);

  // A late answer never replaces a newer one.
  useEffect(() => {
    if (!open || !branchId) return;
    const id = requestRef.current + 1;
    requestRef.current = id;
    setLoading(true);
    authCtx.jwtInst({ method: 'get', url: api, params })
      .then((res) => { if (requestRef.current === id) { setData(res.data); setError(''); } })
      .catch(() => { if (requestRef.current === id) setError(t('mis.plCollectiveLoadFailed')); })
      .finally(() => { if (requestRef.current === id) setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, branchId, params, refreshKey, api, authCtx]);

  const allColumns = useMemo(() => (data ? data.columns : []), [data]);
  const shown = useMemo(() => allColumns.filter((c) => (chosen ? chosen.includes(c.key) : c.on)), [allColumns, chosen]);
  const shownKeys = shown.map((c) => c.key);

  const toggleColumn = (key) => {
    const has = shownKeys.includes(key);
    if (has && shownKeys.length === 1) return;     // a table with no columns shows nothing and prints nothing
    setChosen(has ? shownKeys.filter((k) => k !== key) : [...shownKeys, key]);
  };
  const toggleSort = (column) => {
    if (column.nosort) return;
    setSort((s) => (s.key === column.key ? { key: s.key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: column.key, dir: 'asc' }));
  };

  const print = async () => {
    if (printing) return;
    setPrinting(true); setError('');
    try {
      const caption = captionFor(printLang);
      const res = await authCtx.jwtInst({
        method: 'get', url: `${api}/html`, responseType: 'text',
        params: { ...params, lang: printLang, columns: shownKeys.join(','), ...(caption ? { caption } : {}) },
      });
      printHtmlDocument(res.data);
    } catch (_) {
      setError(t('mis.plCollectivePrintFailed'));
    } finally {
      setPrinting(false);
    }
  };

  const rows = data ? data.rows : [];
  const HEAD_BG = isDark ? '#181818' : '#f4f5f7';
  const cellSx = (c) => ({
    px: 1, py: 0.75, borderBottom: `1px solid ${T.BD}`, borderInlineEnd: `1px solid ${T.BD}`, color: T.TEXT_PRI,
    whiteSpace: c.nowrap || c.num || c.ltr ? 'nowrap' : 'normal', textAlign: c.num || c.ltr ? 'center' : 'start',
    fontVariantNumeric: c.num ? 'tabular-nums' : 'normal', unicodeBidi: 'plaintext',
    minWidth: c.num || c.nowrap ? 0 : 96, maxWidth: 260,
  });
  const iconButtonSx = { width: isMob ? 40 : 30, height: isMob ? 40 : 30, flexShrink: 0, borderRadius: '9px', border: `1px solid ${T.BD}` };

  return (
    <Dialog open={open} onClose={onClose} fullScreen
      PaperProps={{ 'data-testid': 'pl-collective', sx: { bgcolor: T.PANEL_BG, backgroundImage: 'none', display: 'flex', flexDirection: 'column' } }}>

      {/* The bar: what this is, the search, the filters, the columns, the print language, print */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, px: { xs: 1.5, md: 2 }, py: 1, borderBottom: `1px solid ${T.BD}`, flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: T.TEXT_PRI, marginInlineEnd: 1 }}>{t('mis.plCollectiveTitle')}</Typography>
        <Typography data-testid="pl-collective-count" sx={{ fontSize: '0.72rem', color: T.TEXT_TER, marginInlineEnd: 'auto' }}>
          {data ? t('mis.plCollectiveCount', { count: data.total }) : ''}
        </Typography>
        {/* On a phone: the title and the close button, then the search across the width, then the three actions. */}
        <IconButton onClick={onClose} aria-label={t('common.close')} data-testid="pl-collective-close"
          sx={{ width: isMob ? 40 : 30, height: isMob ? 40 : 30, flexShrink: 0, order: { xs: 0, md: 2 } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: { xs: '1 1 100%', md: '0 1 280px' }, order: { xs: 1, md: 0 },
          bgcolor: T.CTRL_BG, border: `1px solid ${T.BD}`, borderRadius: '9px', px: 1, height: isMob ? 40 : 30 }}>
          <SearchIcon sx={{ fontSize: 15, color: T.TEXT_TER }} />
          <InputBase value={search} onChange={(e) => onSearch(e.target.value)} placeholder={t('mis.plSearchPlaceholder')}
            inputProps={{ 'aria-label': t('mis.plSearchPlaceholder'), 'data-testid': 'pl-collective-search' }}
            sx={{ flex: 1, fontSize: '0.78rem', color: T.TEXT_PRI, '& input::placeholder': { color: T.TEXT_TER, opacity: 1 } }} />
          {search && (
            <IconButton size="small" onClick={() => onSearch('')} sx={{ p: 0.25 }} aria-label={t('common.close')}>
              <CloseIcon sx={{ fontSize: 13, color: T.TEXT_TER }} />
            </IconButton>
          )}
        </Box>
        <Tooltip title={t('mis.plFilterOpen')}>
          <IconButton onClick={onOpenFilters} aria-label={t('mis.plFilterOpen')} data-testid="pl-collective-filter" sx={{ ...iconButtonSx, order: 1 }}>
            <Badge badgeContent={filterCount} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 15, minWidth: 15, px: 0.4 } }}>
              <FilterListIcon sx={{ fontSize: 17, color: filterCount ? T.TEXT_PRI : T.TEXT_SEC }} />
            </Badge>
          </IconButton>
        </Tooltip>
        <Tooltip title={t('mis.plCollectiveColumns')}>
          <IconButton onClick={(e) => setColumnsAnchor(e.currentTarget)} aria-label={t('mis.plCollectiveColumns')} data-testid="pl-collective-columns" sx={{ ...iconButtonSx, order: 1 }}>
            <ViewColumnOutlinedIcon sx={{ fontSize: 17, color: T.TEXT_SEC }} />
          </IconButton>
        </Tooltip>
        <Select size="small" value={printLang} onChange={(e) => setPrintChoice(e.target.value)}
          inputProps={{ 'aria-label': t('mis.plCollectivePrintLanguage') }}
          SelectDisplayProps={{ 'data-testid': 'pl-collective-print-lang', title: t('mis.plCollectivePrintLanguage') }}
          sx={{ height: isMob ? 40 : 30, minWidth: 86, flexShrink: 0, order: 1, borderRadius: '8px', fontSize: isMob ? '0.8rem' : '0.72rem' }}>
          {PRINT_LANGS.map(([code, name]) => <MenuItem key={code} value={code} sx={{ fontSize: '0.78rem' }}>{name}</MenuItem>)}
        </Select>
        {/* Print waits for the table to catch up with the search box: the sheet is what is on screen, nothing newer. */}
        <Button variant="contained" size="small" onClick={print} disabled={printing || loading || search !== debouncedSearch || !rows.length} data-testid="pl-collective-print"
          startIcon={printing ? <CircularProgress size={13} color="inherit" /> : <PrintIcon sx={{ fontSize: 15 }} />}
          sx={{ height: isMob ? 40 : 30, borderRadius: '8px', fontSize: '0.72rem', textTransform: 'none', fontWeight: 700, flexShrink: 0, order: 1,
            flexGrow: { xs: 1, md: 0 }, '& .MuiButton-startIcon': { mx: 0, marginInlineEnd: 0.75 } }}>
          {t('mis.plCollectivePrint')}
        </Button>
      </Box>

      {activeFilters}
      {error && <Alert severity="error" sx={{ borderRadius: 0, fontSize: '0.75rem', py: 0.25 }}>{error}</Alert>}
      {data && data.truncated && (
        <Alert severity="warning" sx={{ borderRadius: 0, fontSize: '0.75rem', py: 0.25 }}>{t('mis.plCollectiveTruncated', { count: data.total })}</Alert>
      )}

      {/* The table scrolls both ways under its own headings */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', opacity: loading && data ? 0.6 : 1, transition: 'opacity 0.15s' }}>
        {!data && loading && <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={22} /></Box>}
        {data && rows.length === 0 && (
          <Typography sx={{ p: 6, textAlign: 'center', fontSize: '0.8rem', color: T.TEXT_SEC }}>{t('mis.plNoneMatch')}</Typography>
        )}
        {data && rows.length > 0 && (
          <Box component="table" data-testid="pl-collective-table" sx={{ minWidth: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.76rem' }}>
            <Box component="thead">
              <Box component="tr">
                {shown.map((c) => {
                  const active = sort.key === c.key;
                  return (
                    <Box component="th" key={c.key} data-column={c.key} onClick={() => toggleSort(c)}
                      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                      sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: HEAD_BG, px: 1, py: 0.9, whiteSpace: 'nowrap', textAlign: 'center',
                        fontSize: '0.68rem', fontWeight: 700, color: T.TEXT_PRI, cursor: c.nosort ? 'default' : 'pointer', userSelect: 'none',
                        borderBottom: `2px solid ${T.BD2}`, borderInlineEnd: `1px solid ${T.BD}` }}>
                      {c.label}
                      {active && (sort.dir === 'asc'
                        ? <ArrowUpwardIcon sx={{ fontSize: 12, verticalAlign: 'middle', marginInlineStart: 0.4 }} />
                        : <ArrowDownwardIcon sx={{ fontSize: 12, verticalAlign: 'middle', marginInlineStart: 0.4 }} />)}
                    </Box>
                  );
                })}
              </Box>
            </Box>
            <Box component="tbody">
              {rows.map((row) => (
                <Box component="tr" key={row.id} data-zebra-row="" data-list-row={row.cells.packing} onClick={() => onOpenList(row.id)}
                  sx={{ cursor: 'pointer', '&:hover td': { bgcolor: T.CTRL_BG } }}>
                  {shown.map((c) => (
                    <Box component="td" key={c.key} dir={c.ltr ? 'ltr' : undefined} sx={cellSx(c)}>{row.cells[c.key]}</Box>
                  ))}
                </Box>
              ))}
            </Box>
            {shown.some((c) => c.total) && (
              <Box component="tfoot">
                <Box component="tr" data-testid="pl-collective-total">
                  {shown.map((c, i) => (
                    <Box component="td" key={c.key} sx={{ ...cellSx(c), position: 'sticky', bottom: 0, zIndex: 1, bgcolor: HEAD_BG, fontWeight: 700,
                      borderTop: `2px solid ${T.BD2}`, borderBottom: 'none' }}>
                      {c.total ? data.totals[c.key] : (i === 0 ? t('mis.plCollectiveTotal') : '')}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Which columns: the choice is also what prints */}
      <Popover open={Boolean(columnsAnchor)} anchorEl={columnsAnchor} onClose={() => setColumnsAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ zIndex: (th) => th.zIndex.modal + 2 }}
        PaperProps={{ 'data-testid': 'pl-collective-columns-menu', sx: { width: 270, maxHeight: '70vh', borderRadius: '12px', display: 'flex', flexDirection: 'column' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1, borderBottom: `1px solid ${T.BD}` }}>
          <Typography sx={{ flex: 1, fontSize: '0.78rem', fontWeight: 700, color: T.TEXT_PRI }}>{t('mis.plCollectiveColumns')}</Typography>
          <Button size="small" onClick={() => setChosen(null)} disabled={!chosen} data-testid="pl-collective-columns-reset"
            sx={{ fontSize: '0.68rem', textTransform: 'none', minWidth: 0 }}>
            {t('mis.plCollectiveColumnsDefault')}
          </Button>
        </Box>
        <Box sx={{ overflowY: 'auto', px: 1, py: 0.5 }}>
          {allColumns.map((c) => (
            <FormControlLabel key={c.key} sx={{ m: 0, width: '100%', '& .MuiFormControlLabel-label': { fontSize: '0.78rem', color: T.TEXT_PRI } }}
              label={c.label}
              control={<Checkbox size="small" checked={shownKeys.includes(c.key)} onChange={() => toggleColumn(c.key)}
                inputProps={{ 'data-column-toggle': c.key }} sx={{ p: isMob ? 1 : 0.5 }} />} />
          ))}
        </Box>
      </Popover>
    </Dialog>
  );
}
